/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useRef, useState, useEffect } from 'react';
import { Undo, Redo, Trash2, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface SketchPadProps {
  initialDataUrl?: string;
  onSave: (dataUrl: string) => void;
}

// Visual Palette constants
const PAGE1_COLORS = [
  '#ff3b30', // Red
  '#ff9500', // Orange
  '#ffcc00', // Yellow
  '#34c759', // Light Green
  '#248a3d', // Rich Green
  '#32ade6', // Light Blue
  '#007aff', // Royal Blue
  '#af52de', // Purple
  '#5856d6', // Indigo
  '#ff2d55', // Hot Pink
  '#e0115f', // Deep Ruby
  '#ffffff', // White
  '#cbd5e1', // Light Silver
  '#8e8e93', // Slaty Grey
  '#1c1c1e', // Pitch Black
  '#475569', // Dark Slate
];

const PAGE2_COLORS = [
  '#fadad1', // Pastel Peach
  '#fbf6df', // Pastel Butter
  '#dcf6ef', // Pastel Mint
  '#ebdcf9', // Pastel Lavender
  '#e0f1f8', // Pastel Sky
  '#e1eadb', // Pastel Sage
  '#fadadd', // Pastel Pink Rose
  '#f5ebd6', // Warm Sand
  '#c36241', // Terracotta Clay
  '#808000', // Olive Green
  '#008080', // Teal Blue
  '#000080', // Royal Navy
  '#e1ad01', // Pure Mustard
  '#36454f', // Deep Charcoal
  '#8b5a2b', // Soil Brown
  '#4a2c2a', // Espresso Brown
];

const DEFAULT_RECENTS = [
  '#1c1c1e', // Black
  '#ff3b30', // Red
  '#ff9500', // Orange
  '#ffcc00', // Yellow
  '#34c759', // Green
  '#32ade6', // Teal
  '#007aff', // Blue
  '#af52de'  // Purple
];

// Inline skeuomorphic SVGs for the tools pointing upwards
const PencilTip: React.FC<{ color: string }> = ({ color }) => (
  <svg width="24" height="110" viewBox="0 0 24 110" className="drop-shadow-sm select-none pointer-events-none">
    {/* Lead tip at top (0 to 12px) */}
    <path d="M 12 0 L 15 10 L 9 10 Z" fill={color} />
    {/* Wooden shaved cone (10px to 30px) */}
    <path d="M 9 10 L 15 10 L 19 30 L 5 30 Z" fill="#ebd2b4" />
    <path d="M 11 10 L 13 10 L 14 30 L 10 30 Z" fill="#dfbba2" /> 
    
    {/* Pencil body wood/plastic barrel (30px to 98px) */}
    <rect x="5" y="30" width="14" height="68" fill="#f59e0b" />
    {/* Shadow lines and facets */}
    <rect x="5" y="30" width="3" height="68" fill="#d97706" />
    <rect x="13" y="30" width="2" height="68" fill="#f59e0b" />
    <rect x="15" y="30" width="4" height="68" fill="#b45309" />
    
    {/* Metal band (98px to 104px) */}
    <rect x="5" y="98" width="14" height="6" fill="#94a3b8" />
    <rect x="5" y="100" width="14" height="2" fill="#cbd5e1" />
    
    {/* Pink eraser (104px to 110px) */}
    <path d="M 5 104 L 19 104 A 3 3 0 0 1 19 110 L 5 110 Z" fill="#f472b6" />
    <rect x="5" y="104" width="14" height="6" fill="#fda4af" rx="0.5" />
  </svg>
);

const PenTip: React.FC<{ color: string }> = ({ color }) => (
  <svg width="24" height="110" viewBox="0 0 24 110" className="drop-shadow-sm select-none pointer-events-none">
    {/* Shiny gel ink tip (0px to 8px) */}
    <path d="M 12 0 L 14.5 8 L 9.5 8 Z" fill={color} />
    {/* Metallic silver front cone (8px to 25px) */}
    <path d="M 9.5 8 L 14.5 8 L 17.5 25 L 6.5 25 Z" fill="#cbd5e1" />
    <path d="M 12 8 L 14.5 8 L 15.5 25 L 12 25 Z" fill="#94a3b8" />
    
    {/* Shiny silver accent ring (25px to 30px) */}
    <rect x="5.5" y="25" width="13" height="5" fill="#f1f5f9" />
    <rect x="5.5" y="27" width="13" height="1.5" fill="#cbd5e1" />
    
    {/* Sleek cylindrical plastic body (30px to 110px) */}
    <rect x="5.5" y="30" width="13" height="80" fill="#1e293b" rx="1" />
    {/* Ink Level Status Window on barrel */}
    <rect x="9.5" y="45" width="5" height="25" fill="#0f172a" rx="1" />
    <rect x="9.5" y="50" width="5" height="15" fill={color} opacity="0.8" rx="0.5" />
    {/* Barrel Reflection Overlay */}
    <rect x="6.5" y="30" width="1.5" height="80" fill="#ffffff" opacity="0.12" />
  </svg>
);

const HighlighterTip: React.FC<{ color: string }> = ({ color }) => (
  <svg width="30" height="110" viewBox="0 0 30 110" className="drop-shadow-sm select-none pointer-events-none">
    {/* Broad translucent chisel felt tip (0px to 15px) */}
    <path d="M 10 0 L 22 4 L 20 16 L 10 16 Z" fill={color} fillOpacity="0.85" />
    {/* Highlighter core tip base (15px to 20px) */}
    <rect x="10" y="15" width="10" height="5" fill="#334155" />
    
    {/* Rounded plastic white collar neck (20px to 35px) */}
    <path d="M 10 20 L 20 20 L 23 35 L 7 35 Z" fill="#e2e8f0" />
    <path d="M 15 20 L 20 20 L 22 35 L 15 35 Z" fill="#cbd5e1" />
    
    {/* Wide rectangular highlighter handle (35px to 110px) */}
    <rect x="5" y="35" width="20" height="75" fill="#f8fafc" rx="2" stroke="#e2e8f0" strokeWidth="0.5" />
    {/* Glowing indicator stripe representing the color */}
    <rect x="11" y="50" width="8" height="42" fill={color} fillOpacity="0.8" rx="1" />
    <rect x="6" y="35" width="2.5" height="75" fill="#ffffff" opacity="0.6" />
  </svg>
);

const EraserTipComponent: React.FC = () => (
  <svg width="30" height="110" viewBox="0 0 30 110" className="drop-shadow-sm select-none pointer-events-none">
    {/* Beveled soft rubber eraser top (0px to 40px) */}
    <path d="M 7 10 L 23 0 L 25 40 L 5 40 Z" fill="#ffd1dc" />
    {/* Shading contrast of rubber */}
    <path d="M 5 35 L 25 35 L 25 40 L 5 40 Z" fill="#f43f5e" opacity="0.18" />
    
    {/* Cardboard protective grip wrapping (40px to 110px) */}
    <rect x="4" y="40" width="22" height="70" fill="#334155" rx="1" />
    {/* White stripe indicators */}
    <rect x="7" y="50" width="16" height="4" fill="#ffffff" opacity="0.25" />
    <rect x="7" y="60" width="16" height="20" fill="#1e293b" rx="0.5" />
    <circle cx="15" cy="70" r="3.5" fill="#475569" />
    {/* Sleeve highlights */}
    <rect x="4" y="40" width="1.5" height="70" fill="#ffffff" opacity="0.15" />
  </svg>
);

export const SketchPad: React.FC<SketchPadProps> = ({ initialDataUrl, onSave }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Separate states for tools to recall user customizations
  const [activeTool, setActiveTool] = useState<'pencil' | 'pen' | 'highlighter' | 'eraser'>('pen');
  const [pencilColor, setPencilColor] = useState('#1c1c1e');
  const [pencilWidth, setPencilWidth] = useState(2);
  const [penColor, setPenColor] = useState('#007aff');
  const [penWidth, setPenWidth] = useState(6);
  const [highlighterColor, setHighlighterColor] = useState('#ffcc00');
  const [highlighterWidth, setHighlighterWidth] = useState(24);
  const [eraserWidth, setEraserWidth] = useState(30);

  // Properties Popover toggles
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [colorPage, setColorPage] = useState<0 | 1>(0);
  const [recentlyUsed, setRecentlyUsed] = useState<string[]>(DEFAULT_RECENTS);

  // Drawing canvas states
  const [isDrawing, setIsDrawing] = useState(false);
  const lastCoordinatesRef = useRef<{ x: number; y: number } | null>(null);
  const loadedUrlRef = useRef<string | undefined>(undefined);

  // Undo/Redo historical entries
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);

  // Initialize Canvas backing store on load
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (initialDataUrl === loadedUrlRef.current) {
      return;
    }

    loadedUrlRef.current = initialDataUrl;

    if (!initialDataUrl) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else {
      const img = new Image();
      img.src = initialDataUrl;
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
    }
  }, [initialDataUrl]);

  // Handle recently used additions safely
  const selectColor = (c: string) => {
    // Set for current tool
    if (activeTool === 'pencil') setPencilColor(c);
    else if (activeTool === 'pen') setPenColor(c);
    else if (activeTool === 'highlighter') setHighlighterColor(c);

    // Update recents
    setRecentlyUsed((prev) => {
      const filtered = prev.filter((item) => item.toLowerCase() !== c.toLowerCase());
      return [c, ...filtered].slice(0, 8);
    });
  };

  // Helper resolvers to read current settings of the selected tool
  const getToolColor = () => {
    if (activeTool === 'pencil') return pencilColor;
    if (activeTool === 'pen') return penColor;
    if (activeTool === 'highlighter') return highlighterColor;
    return '#ffffff';
  };

  const getToolWidth = () => {
    if (activeTool === 'pencil') return pencilWidth;
    if (activeTool === 'pen') return penWidth;
    if (activeTool === 'highlighter') return highlighterWidth;
    return eraserWidth;
  };

  const setToolWidth = (val: number) => {
    if (activeTool === 'pencil') setPencilWidth(val);
    else if (activeTool === 'pen') setPenWidth(val);
    else if (activeTool === 'highlighter') setHighlighterWidth(val);
    else if (activeTool === 'eraser') setEraserWidth(val);
  };

  const resetCurrentTool = () => {
    if (activeTool === 'pencil') {
      setPencilColor('#131313');
      setPencilWidth(2);
    } else if (activeTool === 'pen') {
      setPenColor('#007aff');
      setPenWidth(6);
    } else if (activeTool === 'highlighter') {
      setHighlighterColor('#ffcc00');
      setHighlighterWidth(24);
    } else if (activeTool === 'eraser') {
      setEraserWidth(30);
    }
  };

  // Interactive core drawing triggers
  const startDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // Only draw with left click / touch / pen
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Save snapshot to history
    setUndoStack((prev) => [...prev, canvas.toDataURL()]);
    setRedoStack([]);

    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvas.height;

    ctx.beginPath();
    ctx.moveTo(x, y);

    // Apply specific tool context variables
    applyContextProperties(ctx);

    ctx.stroke();
    lastCoordinatesRef.current = { x, y };
    setIsDrawing(true);

    try {
      canvas.setPointerCapture(e.pointerId);
    } catch (err) {
      console.warn('setPointerCapture failed', err);
    }
  };

  const drawAction = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvas.height;

    ctx.beginPath();
    if (lastCoordinatesRef.current) {
      ctx.moveTo(lastCoordinatesRef.current.x, lastCoordinatesRef.current.y);
    } else {
      ctx.moveTo(x, y);
    }

    // Apply specific tool properties dynamically
    applyContextProperties(ctx);

    ctx.lineTo(x, y);
    ctx.stroke();

    lastCoordinatesRef.current = { x, y };
  };

  const applyContextProperties = (ctx: CanvasRenderingContext2D) => {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (activeTool === 'eraser') {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = eraserWidth;
      ctx.globalAlpha = 1.0;
    } else if (activeTool === 'highlighter') {
      ctx.strokeStyle = highlighterColor;
      ctx.lineWidth = highlighterWidth;
      ctx.globalAlpha = 0.4; // Authentic translucent marker glow
    } else if (activeTool === 'pencil') {
      ctx.strokeStyle = pencilColor;
      ctx.lineWidth = pencilWidth;
      ctx.globalAlpha = 0.85; // Felt graphite graphite texture
    } else {
      // Pen
      ctx.strokeStyle = penColor;
      ctx.lineWidth = penWidth;
      ctx.globalAlpha = 1.0; // Sharp ink flow
    }
  };

  const stopDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    setIsDrawing(false);
    lastCoordinatesRef.current = null;
    triggerSave();

    const canvas = canvasRef.current;
    if (canvas) {
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch (err) {}
    }
  };

  const triggerSave = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL();
      loadedUrlRef.current = dataUrl;
      onSave(dataUrl);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setUndoStack((prev) => [...prev, canvas.toDataURL()]);
    setRedoStack([]);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    triggerSave();
  };

  const performUndo = () => {
    if (undoStack.length === 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const currentState = canvas.toDataURL();
    setRedoStack((prev) => [...prev, currentState]);

    const previousState = undoStack[undoStack.length - 1];
    setUndoStack((prev) => prev.slice(0, prev.length - 1));

    const img = new Image();
    img.src = previousState;
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      loadedUrlRef.current = previousState;
      onSave(previousState);
    };
  };

  const performRedo = () => {
    if (redoStack.length === 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const currentState = canvas.toDataURL();
    setUndoStack((prev) => [...prev, currentState]);

    const nextState = redoStack[redoStack.length - 1];
    setRedoStack((prev) => prev.slice(0, prev.length - 1));

    const img = new Image();
    img.src = nextState;
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      loadedUrlRef.current = nextState;
      onSave(nextState);
    };
  };

  // Click tools interaction handler: Single click select, click again properties toggle
  const handleToolClick = (toolName: 'pencil' | 'pen' | 'highlighter' | 'eraser') => {
    if (activeTool !== toolName) {
      setActiveTool(toolName);
      setIsPopoverOpen(false);
    } else {
      setIsPopoverOpen(!isPopoverOpen);
    }
  };

  // Slider specifications custom fitted to each tool context
  const getSliderLimits = () => {
    switch (activeTool) {
      case 'pencil': return { min: 1, max: 10 };
      case 'pen': return { min: 2, max: 24 };
      case 'highlighter': return { min: 10, max: 60 };
      case 'eraser': return { min: 8, max: 90 };
    }
  };

  const currentLimits = getSliderLimits();
  const currentActiveColor = getToolColor();
  const currentActiveWidth = getToolWidth();

  return (
    <div ref={containerRef} className="flex flex-col gap-4 bg-slate-100 dark:bg-slate-905 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 relative select-none w-full shadow-inner" id="interactive-sketch-frame">
      
      {/* 1. VISUAL PAPER CANVAS SHEET ROW */}
      <div className="relative bg-neutral-200/40 dark:bg-slate-950 p-2.5 rounded-xl border border-slate-300 dark:border-slate-850 w-full flex items-center justify-center shadow-inner overflow-hidden">
        <canvas
          ref={canvasRef}
          width={1000}
          height={600}
          onPointerDown={startDrawing}
          onPointerMove={drawAction}
          onPointerUp={stopDrawing}
          onPointerCancel={stopDrawing}
          className="bg-white max-w-full touch-none cursor-crosshair rounded-lg shadow-sm animate-fade-in"
          style={{ aspectRatio: '1000 / 600', width: '100%', height: 'auto' }}
          id="drawing-board-canvas"
        />
      </div>

      {/* 2. UNIFIED AUDIO-DRAW FOOTER TRAY CONTROLLER */}
      <div className="flex items-center justify-between gap-4 px-4 bg-white/70 dark:bg-slate-900/60 backdrop-blur-md border border-slate-200 dark:border-slate-800 rounded-2xl py-3 shadow-md relative" id="drawing-ios-dock">
        
        {/* Left Hand: Classic curved undo & redo arrow buttons */}
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={performUndo}
            disabled={undoStack.length === 0}
            className="p-2 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-800/80 disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer select-none transition-colors"
            title="Undo stroke"
            id="btn-undo-action"
          >
            <Undo className="w-5 h-5 stroke-[2.2]" />
          </button>
          <button
            type="button"
            onClick={performRedo}
            disabled={redoStack.length === 0}
            className="p-2 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-800/80 disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer select-none transition-colors"
            title="Redo stroke"
            id="btn-redo-action"
          >
            <Redo className="w-5 h-5 stroke-[2.2]" />
          </button>
        </div>

        {/* Center: Interactive physical skeuomorphic tools poking up in natural vertical layout */}
        <div className="flex items-end justify-center gap-7 h-20 px-4 relative flex-1">
          
          {/* Pencil (Tool 1) */}
          <button
            type="button"
            onClick={() => handleToolClick('pencil')}
            className="relative focus:outline-none select-none transition-all duration-300 cursor-pointer"
            style={{
              transform: activeTool === 'pencil' ? 'translateY(-20px)' : 'translateY(12px)',
              filter: activeTool === 'pencil' ? 'drop-shadow(0 15px 12px rgba(0,0,0,0.18))' : 'none'
            }}
            title="Graphite Pencil"
          >
            <PencilTip color={pencilColor} />
            {activeTool === 'pencil' && (
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-yellow-400 rounded-full shadow-xs" />
            )}
          </button>

          {/* Pen (Tool 2) */}
          <button
            type="button"
            onClick={() => handleToolClick('pen')}
            className="relative focus:outline-none select-none transition-all duration-300 cursor-pointer"
            style={{
              transform: activeTool === 'pen' ? 'translateY(-20px)' : 'translateY(12px)',
              filter: activeTool === 'pen' ? 'drop-shadow(0 15px 12px rgba(0,0,0,0.18))' : 'none'
            }}
            title="Gel Ink Pen"
          >
            <PenTip color={penColor} />
            {activeTool === 'pen' && (
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-blue-500 rounded-full shadow-xs" />
            )}
          </button>

          {/* Highlighter (Tool 3) */}
          <button
            type="button"
            onClick={() => handleToolClick('highlighter')}
            className="relative focus:outline-none select-none transition-all duration-300 cursor-pointer"
            style={{
              transform: activeTool === 'highlighter' ? 'translateY(-20px)' : 'translateY(12px)',
              filter: activeTool === 'highlighter' ? 'drop-shadow(0 15px 12px rgba(0,0,0,0.18))' : 'none'
            }}
            title="Highlighter Marker"
          >
            <HighlighterTip color={highlighterColor} />
            {activeTool === 'highlighter' && (
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-amber-400 rounded-full shadow-xs" />
            )}
          </button>

          {/* Eraser (Tool 4) */}
          <button
            type="button"
            onClick={() => handleToolClick('eraser')}
            className="relative focus:outline-none select-none transition-all duration-300 cursor-pointer"
            style={{
              transform: activeTool === 'eraser' ? 'translateY(-20px)' : 'translateY(12px)',
              filter: activeTool === 'eraser' ? 'drop-shadow(0 15px 12px rgba(0,0,0,0.18))' : 'none'
            }}
            title="Clean Rubber Eraser"
          >
            <EraserTipComponent />
            {activeTool === 'eraser' && (
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-pink-400 rounded-full shadow-xs" />
            )}
          </button>

        </div>

        {/* Right Hand: Clear Board visual bin button */}
        <div className="flex items-center shrink-0">
          <button
            type="button"
            onClick={clearCanvas}
            className="p-2.5 hover:bg-rose-50 dark:hover:bg-rose-950/20 text-slate-500 hover:text-rose-600 rounded-xl cursor-pointer select-none transition-colors flex items-center justify-center"
            title="Clear canvas"
            id="btn-trash-action"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>

        {/* 3. APPLE NOTES INSPIRED FLOATING POPUP PROPERTIES BUBBLE */}
        <AnimatePresence>
          {isPopoverOpen && (
            <>
              {/* Invisible dismiss overlay wrapper */}
              <div 
                className="fixed inset-0 z-30 bg-transparent cursor-default" 
                onClick={() => setIsPopoverOpen(false)} 
              />
              
              {/* Floating iOS Property Popover Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 15 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="absolute bottom-28 left-1/2 -translate-x-1/2 w-80 bg-white dark:bg-slate-850 rounded-2xl shadow-2xl border border-slate-205/90 dark:border-slate-800 p-4 z-40 flex flex-col text-left"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Popover Header Controls */}
                <div className="flex items-center justify-between text-xs font-sans font-medium text-slate-650 dark:text-zinc-200.5 pb-2 border-b border-slate-100 dark:border-slate-850 select-none">
                  <button 
                    type="button"
                    onClick={resetCurrentTool}
                    className="hover:text-slate-900 dark:hover:text-white cursor-pointer"
                  >
                    Reset
                  </button>
                  <span className="font-bold text-slate-400 uppercase tracking-widest text-[9px]">
                    {activeTool} Properties
                  </span>
                  <button 
                    type="button"
                    onClick={() => setIsPopoverOpen(false)}
                    className="font-semibold text-slate-900 dark:text-white hover:text-blue-600 cursor-pointer"
                  >
                    Done
                  </button>
                </div>

                {/* Only render colors selection panel for drawing lines tools */}
                {activeTool !== 'eraser' && (
                  <>
                    {/* section 1: recently used color list */}
                    <div className="py-2.5 select-none">
                      <span className="text-[10px] font-sans font-bold text-slate-400 uppercase tracking-widest block mb-1.5 text-left select-none">
                        Recently Used
                      </span>
                      <div className="flex items-center gap-1.5">
                        {recentlyUsed.map((colorSample, i) => {
                          const isActive = currentActiveColor.toLowerCase() === colorSample.toLowerCase();
                          return (
                            <button
                              key={`${colorSample}-${i}`}
                              type="button"
                              onClick={() => selectColor(colorSample)}
                              className={`w-6 h-6 rounded-full shrink-0 relative transition-transform hover:scale-110 active:scale-95 focus:outline-none cursor-pointer border border-black/5 dark:border-white/10`}
                              style={{ backgroundColor: colorSample }}
                            >
                              {isActive && (
                                <div className="absolute inset-0 bg-black/15 dark:bg-white/15 rounded-full flex items-center justify-center">
                                  <Check className="w-3.5 h-3.5 text-white dark:text-zinc-900 font-bold stroke-[3]" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="border-t border-slate-100 dark:border-slate-800" />

                    {/* section 2: paginated grid of standard colors selectors */}
                    <div className="py-2.5">
                      <div className="grid grid-cols-8 gap-1.5">
                        {((colorPage === 0 ? PAGE1_COLORS : PAGE2_COLORS)).map((colorHex, index) => {
                          const isSelected = currentActiveColor.toLowerCase() === colorHex.toLowerCase();
                          return (
                            <button
                              key={index}
                              type="button"
                              onClick={() => selectColor(colorHex)}
                              className="w-5.5 h-5.5 rounded-full border border-black/5 dark:border-white/10 relative transition-transform hover:scale-115 active:scale-90 focus:outline-none cursor-pointer"
                              style={{ backgroundColor: colorHex }}
                              title={colorHex}
                            >
                              {isSelected && (
                                <div className="absolute inset-0 bg-black/15 dark:bg-white/15 rounded-full flex items-center justify-center">
                                  <Check className="w-3 h-3 text-white font-extrabold stroke-[3.5]" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* color selection pagination dots (Page 1 static / Page 2 pastel) */}
                      <div className="flex items-center justify-center gap-2 mt-3 mb-1 select-none">
                        <button
                          type="button"
                          onClick={() => setColorPage(0)}
                          className={`w-2 h-2 rounded-full cursor-pointer transition-all ${colorPage === 0 ? 'bg-slate-700 dark:bg-zinc-200' : 'bg-slate-300 dark:bg-slate-700'}`}
                        />
                        <button
                          type="button"
                          onClick={() => setColorPage(1)}
                          className={`w-2 h-2 rounded-full cursor-pointer transition-all ${colorPage === 1 ? 'bg-slate-700 dark:bg-zinc-200' : 'bg-slate-300 dark:bg-slate-700'}`}
                        />
                      </div>
                    </div>

                    <div className="border-t border-slate-100 dark:border-slate-800" />
                  </>
                )}

                {/* section 3: beautiful sine-wave line preview illustrating thickness/translucency */}
                <div className="py-2 bg-slate-50 dark:bg-slate-900/40 rounded-xl my-1.5 flex items-center justify-center relative border border-slate-101 dark:border-slate-850">
                  <svg className="w-full h-11 pointer-events-none" viewBox="0 0 300 60">
                    <path
                      d="M 20 30 C 60 -5, 90 65, 130 30 C 170 -5, 200 65, 240 30 C 265 15, 275 25, 285 30"
                      fill="none"
                      stroke={activeTool === 'eraser' ? '#cbd5e1' : currentActiveColor}
                      strokeWidth={Math.max(1.5, currentActiveWidth / 1.5)}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity={activeTool === 'highlighter' ? 0.45 : (activeTool === 'pencil' ? 0.85 : 1.0)}
                    />
                  </svg>
                  <span className="absolute right-3.5 bottom-1 text-[8px] font-bold text-slate-400 uppercase tracking-widest select-none">
                    Stroke preview
                  </span>
                </div>

                {/* section 4: slider properties adjust wrapper with visual thickness indicator */}
                <div className="pt-2 flex items-center gap-3">
                  {/* Minified tool graphic silhouette */}
                  <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center transform scale-75 overflow-hidden shrink-0 select-none">
                    {activeTool === 'pencil' && <PencilTip color={pencilColor} />}
                    {activeTool === 'pen' && <PenTip color={penColor} />}
                    {activeTool === 'highlighter' && <HighlighterTip color={highlighterColor} />}
                    {activeTool === 'eraser' && <EraserTipComponent />}
                  </div>

                  {/* Dynamic thickness brush blob indicator */}
                  <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center shrink-0">
                    <div 
                      className="rounded-full shadow-inner transition-all duration-100 bg-slate-800 dark:bg-zinc-100/90"
                      style={{ 
                        width: `${Math.max(3, Math.min(26, currentActiveWidth))}px`, 
                        height: `${Math.max(3, Math.min(26, currentActiveWidth))}px`,
                        opacity: activeTool === 'highlighter' ? 0.45 : (activeTool === 'pencil' ? 0.85 : 1.0)
                      }}
                    />
                  </div>

                  {/* Horizontal range thickness slider */}
                  <div className="flex-1 select-none py-1">
                    <input
                      type="range"
                      min={currentLimits.min}
                      max={currentLimits.max}
                      value={currentActiveWidth}
                      onChange={(e) => setToolWidth(parseInt(e.target.value, 10))}
                      className="w-full accent-indigo-600 dark:accent-indigo-400 h-1 bg-slate-200 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer select-none"
                    />
                    <div className="flex justify-between text-[8px] font-bold text-slate-400 uppercase tracking-wider select-none mt-1">
                      <span>Thin</span>
                      <span>{currentActiveWidth}px</span>
                      <span>Thick</span>
                    </div>
                  </div>
                </div>

                {/* Subtle popover caret pointer at the bottom */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white dark:border-t-slate-850" />
              </motion.div>
            </>
          )}
        </AnimatePresence>

      </div>
      
      {/* Dynamic hint message */}
      <p className="text-[10px] text-slate-400/90 dark:text-slate-500 text-center tracking-normal leading-tight font-sans select-none">
        {activeTool === 'eraser' 
          ? 'Single click eraser to select. Double click eraser to adjust wiping scale.' 
          : `Drawing active with ${activeTool}. Double click the active ${activeTool} to customize color or tool size properties.`}
      </p>

    </div>
  );
};
